import { Route, Routes } from "react-router-dom";
import {
  LayoutDashboard,
  Pill,
  HeartPulse,
  ShieldAlert,
  CalendarDays,
  ClipboardList,
  FileText,
  CalendarClock,
  User,
  Users,
  Stethoscope,
  ScrollText,
  Settings,
  Gauge,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { PublicLayout } from "./layouts/PublicLayout";
import { PortalLayout, type PortalNavItem } from "./layouts/PortalLayout";
import { RequireRole } from "./components/RequireRole";

import { HomePage } from "./pages/public/HomePage";
import { AboutPage } from "./pages/public/AboutPage";
import { ServicesPage } from "./pages/public/ServicesPage";
import { HowItWorksPage } from "./pages/public/HowItWorksPage";
import { PricingPage } from "./pages/public/PricingPage";
import { FaqPage } from "./pages/public/FaqPage";
import { ContactPage } from "./pages/public/ContactPage";
import { LoginPage } from "./pages/public/LoginPage";
import { BookReviewPage } from "./pages/public/BookReviewPage";
import { NotFoundPage } from "./pages/public/NotFoundPage";
import { PolicyPage } from "./pages/public/PolicyPage";

import { PatientDashboardPage } from "./pages/patient/PatientDashboardPage";
import { PatientMedicationsPage } from "./pages/patient/PatientMedicationsPage";
import { PatientConditionsPage } from "./pages/patient/PatientConditionsPage";
import { PatientAllergiesPage } from "./pages/patient/PatientAllergiesPage";
import { PatientAppointmentsPage } from "./pages/patient/PatientAppointmentsPage";
import { PatientMtmIntakePage } from "./pages/patient/PatientMtmIntakePage";
import { PatientReportsPage } from "./pages/patient/PatientReportsPage";
import { PatientReportViewPage } from "./pages/patient/PatientReportViewPage";
import { PatientFollowUpPage } from "./pages/patient/PatientFollowUpPage";
import { PatientProfilePage } from "./pages/patient/PatientProfilePage";

import { PharmacistDashboardPage } from "./pages/pharmacist/PharmacistDashboardPage";
import { PharmacistPatientsPage } from "./pages/pharmacist/PharmacistPatientsPage";
import { PharmacistPatientProfilePage } from "./pages/pharmacist/PharmacistPatientProfilePage";
import { PharmacistAssessmentPage } from "./pages/pharmacist/PharmacistAssessmentPage";
import { PharmacistReportPage } from "./pages/pharmacist/PharmacistReportPage";

import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { AdminPharmacistsPage } from "./pages/admin/AdminPharmacistsPage";
import { AdminAppointmentsPage } from "./pages/admin/AdminAppointmentsPage";
import { AdminServicesPage } from "./pages/admin/AdminServicesPage";
import { AdminKpisPage } from "./pages/admin/AdminKpisPage";
import { AdminAuditLogsPage } from "./pages/admin/AdminAuditLogsPage";

function usePatientNav(): PortalNavItem[] {
  const { t } = useTranslation();
  return [
    { key: "dashboard", label: t("patient.nav.dashboard"), to: "/patient", icon: LayoutDashboard },
    { key: "medications", label: t("patient.nav.medications"), to: "/patient/medications", icon: Pill },
    { key: "conditions", label: t("patient.nav.conditions"), to: "/patient/conditions", icon: HeartPulse },
    { key: "allergies", label: t("patient.nav.allergies"), to: "/patient/allergies", icon: ShieldAlert },
    { key: "appointments", label: t("patient.nav.appointments"), to: "/patient/appointments", icon: CalendarDays },
    { key: "mtm", label: t("patient.nav.mtm"), to: "/patient/mtm", icon: ClipboardList },
    { key: "reports", label: t("patient.nav.reports"), to: "/patient/reports", icon: FileText },
    { key: "followUp", label: t("patient.nav.followUp"), to: "/patient/follow-up", icon: CalendarClock },
    { key: "profile", label: t("patient.nav.profile"), to: "/patient/profile", icon: User },
  ];
}

function usePharmacistNav(): PortalNavItem[] {
  const { t } = useTranslation();
  return [
    { key: "dashboard", label: t("pharmacist.nav.dashboard"), to: "/pharmacist", icon: LayoutDashboard },
    { key: "patients", label: t("pharmacist.nav.patients"), to: "/pharmacist/patients", icon: Users },
  ];
}

function useAdminNav(): PortalNavItem[] {
  const { t } = useTranslation();
  return [
    { key: "dashboard", label: t("admin.nav.dashboard"), to: "/admin", icon: LayoutDashboard },
    { key: "users", label: t("admin.nav.users"), to: "/admin/users", icon: Users },
    { key: "pharmacists", label: t("admin.nav.pharmacists"), to: "/admin/pharmacists", icon: Stethoscope },
    { key: "appointments", label: t("admin.nav.appointments"), to: "/admin/appointments", icon: CalendarDays },
    { key: "services", label: t("admin.nav.services"), to: "/admin/services", icon: Settings },
    { key: "kpis", label: t("admin.nav.kpis"), to: "/admin/kpis", icon: Gauge },
    { key: "auditLogs", label: t("admin.nav.auditLogs"), to: "/admin/audit-logs", icon: ScrollText },
  ];
}

function PatientPortal() {
  const navItems = usePatientNav();
  return <PortalLayout navItems={navItems} homeTo="/patient" />;
}
function PharmacistPortal() {
  const navItems = usePharmacistNav();
  return <PortalLayout navItems={navItems} homeTo="/pharmacist" />;
}
function AdminPortal() {
  const navItems = useAdminNav();
  return <PortalLayout navItems={navItems} homeTo="/admin" />;
}

export default function App() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="how-it-works" element={<HowItWorksPage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="book-a-review" element={<BookReviewPage />} />
        <Route path="privacy" element={<PolicyPage variant="privacy" />} />
        <Route path="terms" element={<PolicyPage variant="terms" />} />
      </Route>

      <Route element={<RequireRole roles={["PATIENT"]} />}>
        <Route path="patient" element={<PatientPortal />}>
          <Route index element={<PatientDashboardPage />} />
          <Route path="medications" element={<PatientMedicationsPage />} />
          <Route path="conditions" element={<PatientConditionsPage />} />
          <Route path="allergies" element={<PatientAllergiesPage />} />
          <Route path="appointments" element={<PatientAppointmentsPage />} />
          <Route path="mtm" element={<PatientMtmIntakePage />} />
          <Route path="reports" element={<PatientReportsPage />} />
          <Route path="reports/:assessmentId" element={<PatientReportViewPage />} />
          <Route path="follow-up" element={<PatientFollowUpPage />} />
          <Route path="profile" element={<PatientProfilePage />} />
        </Route>
      </Route>

      <Route element={<RequireRole roles={["PHARMACIST"]} />}>
        <Route path="pharmacist" element={<PharmacistPortal />}>
          <Route index element={<PharmacistDashboardPage />} />
          <Route path="patients" element={<PharmacistPatientsPage />} />
          <Route path="patients/:patientId" element={<PharmacistPatientProfilePage />} />
          <Route path="assessment/:assessmentId" element={<PharmacistAssessmentPage />} />
          <Route path="reports/:assessmentId" element={<PharmacistReportPage />} />
        </Route>
      </Route>

      <Route element={<RequireRole roles={["ADMIN"]} />}>
        <Route path="admin" element={<AdminPortal />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="pharmacists" element={<AdminPharmacistsPage />} />
          <Route path="appointments" element={<AdminAppointmentsPage />} />
          <Route path="services" element={<AdminServicesPage />} />
          <Route path="kpis" element={<AdminKpisPage />} />
          <Route path="audit-logs" element={<AdminAuditLogsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
